// components/RoleModals.tsx
// COMPLETE: All modals with permissions and users UI

import { memo, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  Plus,
  Edit2,
  Save,
  Trash2,
  Copy,
  Lock,
  Users,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Mail,
  Shield,
  Check,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { User } from "../../../types/Role";

// ==================== AVAILABLE PERMISSIONS ====================
// This should ideally come from an API endpoint
const AVAILABLE_PERMISSIONS = {
  pos: {
    displayName: { en: "Point of Sale", ar: "نقطة البيع" },
    actions: ["create", "read", "update", "delete"],
  },
  gifts: {
    displayName: { en: "Gifts", ar: "الهدايا" },
    actions: ["create", "read", "update", "delete"],
  },
  orders: {
    displayName: { en: "Orders", ar: "الطلبات" },
    actions: ["create", "read", "update", "delete", "update-status"],
  },
  products: {
    displayName: { en: "Products", ar: "المنتجات" },
    actions: ["create", "read", "update", "delete"],
  },
  customers: {
    displayName: { en: "Customers", ar: "العملاء" },
    actions: ["create", "read", "update", "delete"],
  },
  inventory: {
    displayName: { en: "Inventory", ar: "المخزون" },
    actions: ["create", "read", "update", "delete"],
  },
  "purchase-returns": {
    displayName: { en: "Purchase Returns", ar: "مرتجعات المشتريات" },
    actions: [
      "create",
      "read",
      "update",
      "approve",
      "complete",
      "cancel",
      "delete",
    ],
  },
  returns: {
    displayName: { en: "Returns & Exchanges", ar: "المرتجعات والاستبدالات" },
    actions: ["read", "update"],
  },
  reports: {
    displayName: { en: "Reports", ar: "التقارير" },
    actions: ["create", "read", "export"],
  },
  expenses: {
    displayName: { en: "Expenses", ar: "المصروفات" },
    actions: ["create", "read", "update", "delete", "approve"],
  },
  invoices: {
    displayName: { en: "Invoices", ar: "الفواتير" },
    actions: ["create", "read", "update", "delete"],
  },
  financial: {
    displayName: { en: "Financial", ar: "المالية" },
    actions: ["create", "read", "update", "delete"],
  },
  delivery: {
    displayName: { en: "Delivery", ar: "التوصيل" },
    actions: ["create", "read", "update", "manage"],
  },
  cart: {
    displayName: { en: "Cart", ar: "السلة" },
    actions: ["create", "read", "update", "delete"],
  },
  profile: {
    displayName: { en: "Profile", ar: "الملف الشخصي" },
    actions: ["read", "update"],
  },
  favorites: {
    displayName: { en: "Favorites", ar: "المفضلة" },
    actions: ["create", "read", "delete"],
  },
  all: {
    displayName: { en: "All Permissions", ar: "جميع الصلاحيات" },
    actions: ["*"],
  },
};

const ACTION_LABELS: { [key: string]: { en: string; ar: string } } = {
  create: { en: "Create", ar: "إنشاء" },
  read: { en: "Read", ar: "قراءة" },
  update: { en: "Update", ar: "تحديث" },
  delete: { en: "Delete", ar: "حذف" },
  approve: { en: "Approve", ar: "موافقة" },
  export: { en: "Export", ar: "تصدير" },
  "update-status": { en: "Update Status", ar: "تحديث الحالة" },
  complete: { en: "Complete", ar: "إكمال" },
  cancel: { en: "Cancel", ar: "إلغاء" },
  manage: { en: "Manage", ar: "إدارة" },
  "read:own": { en: "Read Own", ar: "قراءة الخاص" },
  "*": { en: "All Actions", ar: "جميع الإجراءات" },
};

// ==================== MAIN MODALS WRAPPER ====================
export const RoleModals = memo(
  ({
    modals,
    currentRole,
    formData,
    saving,
    permissionSearch,
    setPermissionSearch,
    roleUsers,
    loadingUsers,
    updateFormData,
    onClose,
    onCreate,
    onUpdate,
    onDelete,
    onUpdatePermissions,
    onDuplicate,
    toggleModulePermission,
    toggleAllModulePermissions,
    selectAllPermissions,
    clearAllPermissions,
  }: any) => {
    return (
      <>
        {modals.create && (
          <CreateEditModal
            isOpen={modals.create}
            mode="create"
            formData={formData}
            saving={saving}
            updateFormData={updateFormData}
            onClose={() => onClose("create")}
            onSave={onCreate}
            toggleModulePermission={toggleModulePermission}
            toggleAllModulePermissions={toggleAllModulePermissions}
            selectAllPermissions={selectAllPermissions}
            clearAllPermissions={clearAllPermissions}
          />
        )}

        {modals.edit && currentRole && (
          <CreateEditModal
            isOpen={modals.edit}
            mode="edit"
            role={currentRole}
            formData={formData}
            saving={saving}
            updateFormData={updateFormData}
            onClose={() => onClose("edit")}
            onSave={onUpdate}
            toggleModulePermission={toggleModulePermission}
            toggleAllModulePermissions={toggleAllModulePermissions}
            selectAllPermissions={selectAllPermissions}
            clearAllPermissions={clearAllPermissions}
          />
        )}

        {modals.delete && currentRole && (
          <DeleteModal
            isOpen={modals.delete}
            role={currentRole}
            saving={saving}
            onClose={() => onClose("delete")}
            onConfirm={onDelete}
          />
        )}

        {modals.duplicate && currentRole && (
          <DuplicateModal
            isOpen={modals.duplicate}
            role={currentRole}
            saving={saving}
            onClose={() => onClose("duplicate")}
            onConfirm={onDuplicate}
          />
        )}

        {modals.users && currentRole && (
          <UsersModal
            isOpen={modals.users}
            role={currentRole}
            users={roleUsers}
            loading={loadingUsers}
            onClose={() => onClose("users")}
          />
        )}

        {modals.permissions && currentRole && (
          <PermissionsModal
            isOpen={modals.permissions}
            role={currentRole}
            formData={formData}
            saving={saving}
            permissionSearch={permissionSearch}
            setPermissionSearch={setPermissionSearch}
            onClose={() => onClose("permissions")}
            onSave={onUpdatePermissions}
            toggleModulePermission={toggleModulePermission}
            toggleAllModulePermissions={toggleAllModulePermissions}
            selectAllPermissions={selectAllPermissions}
            clearAllPermissions={clearAllPermissions}
          />
        )}
      </>
    );
  },
);

// ==================== CREATE/EDIT MODAL ====================

const CreateEditModal = memo(
  ({
    isOpen,
    mode,
    role,
    formData,
    saving,
    updateFormData,
    onClose,
    onSave,
    toggleModulePermission,
    toggleAllModulePermissions,
    selectAllPermissions,
    clearAllPermissions,
  }: any) => {
    const { t } = useTranslation();
    // const isArabic = i18n.language === "ar";
    const [showPermissions, setShowPermissions] = useState(false);

    if (!isOpen) return null;

    const isCreate = mode === "create";

    const handleSave = () => {
      try {
        if (!formData?.name?.trim()) {
          console.error("Role name is required");
          return;
        }
        onSave();
      } catch (error) {
        console.error("Error saving role:", error);
      }
    };

    // ✅ Add safety check for formData
    if (!formData) {
      console.error("FormData is undefined");
      return null;
    }

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              {isCreate ? (
                <Plus className="w-6 h-6" />
              ) : (
                <Edit2 className="w-6 h-6" />
              )}
              {isCreate ? t("Create New Role") : t("Edit Role")}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {!isCreate && role?.isSystem && (
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-purple-900">
                    {t("System Role")}
                  </p>
                  <p className="text-xs text-purple-700 mt-1">
                    {t("This is a system role. Some fields may be restricted.")}
                  </p>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                {t("Basic Information")}
              </h4>

              {/* Role Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Role Name")} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateFormData({ name: e.target.value })}
                  placeholder={t("Enter role name (e.g., manager, cashier)")}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none"
                  autoFocus
                />
              </div>

              {/* Display Names */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Display Name (English)")}
                  </label>
                  <input
                    type="text"
                    value={formData.displayName.en}
                    onChange={(e) =>
                      updateFormData({
                        displayName: {
                          ...formData.displayName,
                          en: e.target.value,
                        },
                      })
                    }
                    placeholder="Administrator"
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Display Name (Arabic)")}
                  </label>
                  <input
                    type="text"
                    value={formData.displayName.ar}
                    onChange={(e) =>
                      updateFormData({
                        displayName: {
                          ...formData.displayName,
                          ar: e.target.value,
                        },
                      })
                    }
                    placeholder="مدير النظام"
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none"
                    dir="rtl"
                  />
                </div>
              </div>

              {/* Descriptions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Description (English)")}
                  </label>
                  <textarea
                    value={formData.description.en}
                    onChange={(e) =>
                      updateFormData({
                        description: {
                          ...formData.description,
                          en: e.target.value,
                        },
                      })
                    }
                    placeholder="Describe this role..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {t("Description (Arabic)")}
                  </label>
                  <textarea
                    value={formData.description.ar}
                    onChange={(e) =>
                      updateFormData({
                        description: {
                          ...formData.description,
                          ar: e.target.value,
                        },
                      })
                    }
                    placeholder="وصف هذا الدور..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none resize-none"
                    dir="rtl"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t("Status")}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => updateFormData({ isActive: true })}
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all active:scale-95 ${
                      formData.isActive
                        ? "bg-green-50 border-green-500 text-green-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <CheckCircle className="inline mr-2" size={16} />
                    {t("Active")}
                  </button>
                  <button
                    type="button"
                    onClick={() => updateFormData({ isActive: false })}
                    className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all active:scale-95 ${
                      !formData.isActive
                        ? "bg-red-50 border-red-500 text-red-700"
                        : "border-gray-200 text-gray-500 hover:border-gray-300"
                    }`}
                  >
                    <XCircle className="inline mr-2" size={16} />
                    {t("Inactive")}
                  </button>
                </div>
              </div>
            </div>

            {/* Permissions Section */}
            <div className="bg-gray-50 rounded-lg p-4">
              <button
                onClick={() => setShowPermissions(!showPermissions)}
                className="w-full flex items-center justify-between text-left font-semibold text-gray-900"
              >
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  {t("Permissions & Features")}
                  <span className="text-sm font-normal text-gray-600">
                    ({Object.keys(formData.permissions).length}{" "}
                    {t("modules selected")})
                  </span>
                </div>
                {showPermissions ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
              </button>

              {showPermissions && (
                <div className="mt-4">
                  <PermissionsSelector
                    permissions={formData.permissions}
                    toggleModulePermission={toggleModulePermission}
                    toggleAllModulePermissions={toggleAllModulePermissions}
                    selectAllPermissions={selectAllPermissions}
                    clearAllPermissions={clearAllPermissions}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white border-t pt-4">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all active:scale-95"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !formData.name.trim()}
              className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{isCreate ? t("Creating...") : t("Saving...")}</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isCreate ? t("Create Role") : t("Save Changes")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

// components/RoleModals_Part2.tsx
// Continuation: Permissions Selector, Users Modal, Delete, Duplicate modals

// ==================== PERMISSIONS SELECTOR ====================
const PermissionsSelector = memo(
  ({
    permissions,
    toggleModulePermission,
    toggleAllModulePermissions,
    selectAllPermissions,
    clearAllPermissions,
  }: any) => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === "ar";
    const [expandedModules, setExpandedModules] = useState<Set<string>>(
      new Set(),
    );
    const safePermissions = permissions || {};

    const toggleModule = (module: string) => {
      setExpandedModules((prev) => {
        const next = new Set(prev);
        if (next.has(module)) {
          next.delete(module);
        } else {
          next.add(module);
        }
        return next;
      });
    };

    const allModulesData = useMemo(() => {
      const data: { [key: string]: string[] } = {};
      Object.keys(AVAILABLE_PERMISSIONS).forEach((module) => {
        data[module] =
          AVAILABLE_PERMISSIONS[
            module as keyof typeof AVAILABLE_PERMISSIONS
          ].actions;
      });
      return data;
    }, []);

    const totalSelected = Object.values(safePermissions).reduce(
      (sum: number, actions: any) => {
        if (!Array.isArray(actions)) return sum;
        return sum + actions.length;
      },
      0,
    );
    const totalAvailable = Object.values(allModulesData).reduce(
      (sum: number, actions: any) => sum + actions.length,
      0,
    );

    return (
      <div className="space-y-4">
        {/* Quick Actions */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => selectAllPermissions(allModulesData)}
            className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-semibold text-sm transition-all active:scale-95"
          >
            <CheckCircle className="w-4 h-4 inline mr-1" />
            {t("Select All")}
          </button>
          <button
            onClick={clearAllPermissions}
            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-semibold text-sm transition-all active:scale-95"
          >
            <XCircle className="w-4 h-4 inline mr-1" />
            {t("Clear All")}
          </button>
          <div className="ml-auto text-sm text-gray-600 flex items-center gap-2">
            <span className="font-semibold">{totalSelected}</span>
            <span>/</span>
            <span>{totalAvailable}</span>
            <span>{t("selected")}</span>
          </div>
        </div>

        {/* Permissions List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {Object.entries(AVAILABLE_PERMISSIONS).map(([module, config]) => {
            const modulePerms = safePermissions[module] || [];

            // ✅ Add validation for config.actions
            if (!config.actions || !Array.isArray(config.actions)) {
              return null;
            }

            const allSelected = config.actions.every(
              (action) =>
                Array.isArray(modulePerms) && modulePerms.includes(action),
            );
            const someSelected = config.actions.some(
              (action) =>
                Array.isArray(modulePerms) && modulePerms.includes(action),
            );
            const isExpanded = expandedModules.has(module);

            return (
              <div
                key={module}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Module Header */}
                <div
                  className={`p-4 cursor-pointer transition-colors ${
                    someSelected ? "bg-indigo-50" : "bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div
                      className="flex items-center gap-3 flex-1"
                      onClick={() => toggleModule(module)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                      <div>
                        <h5 className="font-semibold text-gray-900 capitalize">
                          {isArabic
                            ? config.displayName.ar
                            : config.displayName.en}
                        </h5>
                        <p className="text-xs text-gray-500">
                          {modulePerms.length} / {config.actions.length}{" "}
                          {t("actions")}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleAllModulePermissions(module, config.actions);
                      }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all active:scale-95 ${
                        allSelected
                          ? "bg-indigo-600 text-white"
                          : someSelected
                            ? "bg-indigo-100 text-indigo-700"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                    >
                      {allSelected ? (
                        <>
                          <Check className="w-4 h-4 inline mr-1" />
                          {t("All")}
                        </>
                      ) : someSelected ? (
                        t("Some")
                      ) : (
                        t("None")
                      )}
                    </button>
                  </div>
                </div>

                {/* Actions List */}
                {isExpanded && (
                  <div className="p-4 pt-0 bg-gray-50 border-t border-gray-600">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mt-2">
                      {config.actions.map((action) => {
                        const isSelected = modulePerms.includes(action);
                        return (
                          <button
                            key={action}
                            onClick={() =>
                              toggleModulePermission(module, action)
                            }
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all active:scale-95 ${
                              isSelected
                                ? "bg-indigo-600 text-white"
                                : "bg-white text-gray-700 border border-gray-200 hover:border-indigo-300"
                            }`}
                          >
                            {isSelected && (
                              <Check className="w-3 h-3 inline mr-1" />
                            )}
                            {isArabic
                              ? ACTION_LABELS[action]?.ar || action
                              : ACTION_LABELS[action]?.en || action}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

// ==================== DELETE MODAL ====================
const DeleteModal = memo(
  ({ isOpen, role, saving, onClose, onConfirm }: any) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-gray-700 to-blackpx-6 py-4 rounded-t-2xl flex items-center gap-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <Trash2 className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">{t("Delete Role")}</h3>
          </div>

          <div className="p-6">
            <p className="text-gray-700 mb-4">
              {t("Are you sure you want to delete the role")}{" "}
              <strong className="capitalize text-gray-900">{role.name}</strong>?
            </p>

            {(role.userCount || 0) > 0 && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-red-900 font-semibold">
                      {t("Warning")}: {role.userCount}{" "}
                      {t("user(s) currently have this role.")}
                    </p>
                    <p className="text-xs text-red-700 mt-1">
                      {t("They will lose this role's permissions.")}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
              <strong>{t("Note")}:</strong> {t("This action cannot be undone.")}
            </p>
          </div>

          <div className="px-6 pb-6 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all active:scale-95"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={onConfirm}
              disabled={saving}
              className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t("Deleting...")}</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  {t("Delete Role")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

// ==================== DUPLICATE MODAL ====================
const DuplicateModal = memo(
  ({ isOpen, role, saving, onClose, onConfirm }: any) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center gap-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <Copy className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">
              {t("Duplicate Role")}
            </h3>
          </div>

          <div className="p-6">
            <p className="text-gray-700 mb-4">
              {t("Are you sure you want to duplicate the role")}{" "}
              <strong className="capitalize text-gray-900">{role.name}</strong>?
            </p>
            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Copy className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-purple-900 font-semibold">
                    {t(
                      "A new role will be created with the same permissions and settings.",
                    )}
                  </p>
                  <p className="text-xs text-purple-700 mt-1">
                    {t("You can modify it after creation.")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all active:scale-95"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={onConfirm}
              disabled={saving}
              className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t("Duplicating...")}</span>
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5" />
                  {t("Duplicate")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

// ==================== USERS MODAL ====================
const UsersModal = memo(({ isOpen, role, users, loading, onClose }: any) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Users className="w-6 h-6" />
            <span className="truncate capitalize">
              {t("Users with Role")} -{" "}
              {role.displayName?.en || role.displayName?.ar || role.name}
            </span>
          </h3>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors flex-shrink-0 ml-2"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {t("No users found with this role")}
              </p>
              <p className="text-gray-400 text-sm mt-2">
                {t("Assign this role to users to see them here")}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user: User) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">
                          {user.name?.charAt(0)?.toUpperCase() || "U"}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      user.status === "active"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {user.status || "Active"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 sticky bottom-0 bg-white border-t pt-4">
          <button
            onClick={onClose}
            className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all active:scale-95"
          >
            {t("Close")}
          </button>
        </div>
      </div>
    </div>
  );
});

// ==================== PERMISSIONS MODAL ====================
const PermissionsModal = memo(
  ({
    isOpen,
    role,
    formData,
    saving,
    permissionSearch,
    setPermissionSearch,
    onClose,
    onSave,
    toggleModulePermission,
    toggleAllModulePermissions,
    selectAllPermissions,
    clearAllPermissions,
  }: any) => {
    const { t } = useTranslation();

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-700 to-black px-6 py-4 rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Lock className="w-6 h-6" />
              <span className="truncate capitalize">
                {t("Manage Permissions")} - {role.displayName?.en || role.name}
              </span>
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors flex-shrink-0 ml-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <PermissionsSelector
              permissions={formData.permissions}
              toggleModulePermission={toggleModulePermission}
              toggleAllModulePermissions={toggleAllModulePermissions}
              selectAllPermissions={selectAllPermissions}
              clearAllPermissions={clearAllPermissions}
            />
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 flex gap-3 sticky bottom-0 bg-white border-t pt-4">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all active:scale-95"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className="flex-1 bg-[#0d223f] text-white px-6 py-3 rounded-lg hover:bg-[#0d223f] font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t("Saving...")}</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {t("Update Permissions")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

export default RoleModals;
